import {
  GRAPHQL_WEBSOCKET_STATE,
  listenGraphQLWebsocket,
  isGraphQLWebsocketConnected,
} from "@/store/api";
import { isFunction } from "@/helperMethods/util";

/**
 * Whether the livechat socket is open right now.
 *
 * Reading the status touches the subscription client, which does not exist until the Apollo client
 * has been built. Guessing "not yet connected" is the safe answer: the worst case is a missing
 * toast, never a missing refetch.
 */
function socketIsOpen(): boolean {
  try {
    return isGraphQLWebsocketConnected();
  } catch (error) {
    return false;
  }
}

/**
 * Runs a view's recovery work whenever the livechat socket (re)connects.
 *
 * Two events matter. RECONNECTED covers every drop the client recovers from; CONNECTED is emitted
 * once, for the first socket of the page.
 *
 * The distinction that actually bites is mount order, not the event name. The client is created
 * with `lazy: true`, so the socket is normally already open before a view mounts and the first
 * event that view ever sees is a genuine reconnect. Treating it as the page's first connect made
 * the supervisor monitor skip its refetch on the first real drop after a page load (KR-10982).
 *
 * So the recovery work runs on EVERY event. `isRecovery` — seeded from whether the socket was
 * already up when the view mounted — only decides whether to announce it to the agent.
 */
export default {
  data() {
    return {
      // Whether this view has ever seen the socket connected. Seeded at mount, not assumed false.
      socketSeenConnected: false,
      socketRecoveryUnsubscribes: [],
    };
  },
  methods: {
    listenForSocketRecovery(onConnected: (context: { isRecovery: boolean }) => void) {
      const component = this as any;
      component.socketSeenConnected = socketIsOpen();

      const handler = () => {
        const isRecovery = component.socketSeenConnected;
        component.socketSeenConnected = true;
        onConnected({ isRecovery });
      };

      component.socketRecoveryUnsubscribes = [
        listenGraphQLWebsocket(GRAPHQL_WEBSOCKET_STATE.CONNECTED, handler),
        listenGraphQLWebsocket(GRAPHQL_WEBSOCKET_STATE.RECONNECTED, handler),
      ];
    },
    stopListeningForSocketRecovery() {
      const component = this as any;
      component.socketRecoveryUnsubscribes.forEach(
        (unsubscribe) => isFunction(unsubscribe) && unsubscribe()
      );
      component.socketRecoveryUnsubscribes = [];
    },
  },
};
